/**
 * Datas bloqueadas da agenda — parsing compartilhado.
 *
 * Existe porque a mesma configuração (`clinics/{cid}/config/agenda.blockedDates`)
 * era interpretada de dois jeitos diferentes:
 *
 *   - o BACKEND do portal (functions/src/proxy/portal.ts, `getAgendaConfig`)
 *     respeita o `scope` e bloqueia só a faixa indicada;
 *   - a AGENDA INTERNA lia apenas `{date, reason}` e descartava o `scope`,
 *     tratando QUALQUER bloqueio como dia inteiro.
 *
 * Na prática: bloquear a manhã fazia a secretária ver o dia todo fechado —
 * sem conseguir clicar pra criar nem arrastar card — enquanto o paciente
 * continuava agendando à tarde pelo portal. Este módulo é a fonte única do
 * lado do cliente; o backend mantém sua cópia porque roda em outro runtime.
 *
 * Formatos aceitos (os dois convivem na base por retrocompatibilidade):
 *   - `'2026-12-25'` (string solta) → dia inteiro;
 *   - `{ date, reason?, scope?, start?, end? }`.
 */

export type BlockScope = 'FULL' | 'MORNING' | 'AFTERNOON' | 'CUSTOM';

/** Faixa bloqueada dentro de uma data. `end` é EXCLUSIVO. */
export interface BlockedRange {
  start: string; // 'HH:MM'
  end: string;   // 'HH:MM'
  /** true = dia inteiro. Atalho pra não ter que comparar 00:00–23:59. */
  full: boolean;
  reason?: string;
}

/** Default do backend (`DEFAULT_SHIFT_BOUNDARY` em portal.ts). Manter em
 *  sincronia: é ele que decide onde termina a "manhã". */
export const DEFAULT_SHIFT_BOUNDARY = '12:00';

/** 'HH:MM' → minutos desde a meia-noite. */
export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Constrói `Map<'YYYY-MM-DD', BlockedRange[]>` a partir do campo cru.
 *
 * Múltiplas entradas na mesma data coexistem (ex.: manhã bloqueada + uma
 * faixa custom à tarde) — a UI de configuração hoje só cria uma por data,
 * mas o formato suporta e o backend já trata assim.
 */
export function parseBlockedDates(
  raw: unknown,
  shiftBoundary: string = DEFAULT_SHIFT_BOUNDARY,
): Map<string, BlockedRange[]> {
  const out = new Map<string, BlockedRange[]>();
  if (!Array.isArray(raw)) return out;

  for (const entry of raw) {
    let dateStr: string | null = null;
    let range: BlockedRange | null = null;

    if (typeof entry === 'string') {
      dateStr = entry;
      range = { start: '00:00', end: '23:59', full: true };
    } else if (entry && typeof entry === 'object' && typeof (entry as { date?: unknown }).date === 'string') {
      const e = entry as { date: string; reason?: string; scope?: BlockScope; start?: string; end?: string };
      dateStr = e.date;
      const scope: BlockScope = e.scope ?? 'FULL';
      if (scope === 'MORNING') {
        range = { start: '00:00', end: shiftBoundary, full: false, reason: e.reason };
      } else if (scope === 'AFTERNOON') {
        range = { start: shiftBoundary, end: '23:59', full: false, reason: e.reason };
      } else if (scope === 'CUSTOM' && e.start && e.end) {
        range = { start: e.start, end: e.end, full: false, reason: e.reason };
      } else {
        // Inclui CUSTOM sem start/end — tratar como dia inteiro é o lado
        // seguro: fecha demais em vez de liberar o que a clínica fechou.
        range = { start: '00:00', end: '23:59', full: true, reason: e.reason };
      }
    }

    if (dateStr && range) {
      const arr = out.get(dateStr) ?? [];
      arr.push(range);
      out.set(dateStr, arr);
    }
  }
  return out;
}

/** True quando alguma faixa cobre o dia inteiro. */
export function isDateFullyBlocked(ranges: BlockedRange[] | undefined): boolean {
  return !!ranges?.some((r) => r.full);
}

/** True quando o horário 'HH:MM' cai em alguma faixa bloqueada da data. */
export function isTimeBlocked(hhmm: string, ranges: BlockedRange[] | undefined): boolean {
  if (!ranges?.length) return false;
  const min = timeToMinutes(hhmm);
  return ranges.some((r) => min >= timeToMinutes(r.start) && min < timeToMinutes(r.end));
}

/** Rótulo curto da faixa, pra tooltip e listas. */
export function describeRanges(ranges: BlockedRange[] | undefined): string {
  if (!ranges?.length) return '';
  if (isDateFullyBlocked(ranges)) return 'Dia inteiro';
  return ranges.map((r) => `${r.start}–${r.end}`).join(', ');
}

/** Primeiro motivo informado entre as faixas (a UI só coleta um). */
export function firstReason(ranges: BlockedRange[] | undefined): string | undefined {
  return ranges?.find((r) => r.reason)?.reason;
}

// ─────────────────────────────────────────────────────────────────────
// Liberação de agenda (modo "tudo fechado, o médico libera").
//
// No modelo padrão a agenda nasce ABERTA e a clínica fecha por exceção
// (`blockedDates`). O consultório do Leopoldo funciona ao contrário: os
// horários ficam fechados até ele liberar (~1 mês à frente, por turno ou
// "até tal hora"), porque viagens e atendimentos em outros locais caem em
// datas variáveis. Com `releaseMode` ligado, tudo que NÃO está em
// `releases` vira faixa bloqueada calculada — assim grade, clique-pra-
// criar, "próximo disponível" e o portal respeitam sem código novo.
//
// Formato de `clinics/{cid}/config/agenda.releases[]`:
//   { date, scope: FULL|MORNING|AFTERNOON|CUSTOM, start?, end?,
//     returnsMax?: number,      // teto de retornos no dia (secretária)
//     procedures?: boolean,     // dia de cirurgia/procedimento
//     reserved?: boolean }      // reserva: só a secretária marca; o portal
//                               // não vê (pacientes vindos de fora, cujos
//                               // nomes chegam só no dia)
// Uma entrada por data na UI; o parser aceita várias (união).
// ─────────────────────────────────────────────────────────────────────

/** Motivo padrão das faixas calculadas — a UI usa pra distinguir de
 *  bloqueio manual (banner, cor, lista de bloqueios do escopo). */
export const UNRELEASED_REASON = 'Não liberado';

export interface ReleaseEntry {
  date: string;
  scope: BlockScope;
  start?: string;
  end?: string;
  returnsMax?: number;
  procedures?: boolean;
  /** Reserva da secretária: a faixa conta como liberada pra agenda
   *  interna, mas o portal a trata como bloqueada. */
  reserved?: boolean;
  /**
   * De quem é esta liberação (spec 010, fase 4). **Ausente = vale pra
   * todos os médicos** — é o que toda liberação feita até aqui significa,
   * e por isso o campo é opcional em vez de obrigatório.
   */
  professionalId?: string;
  /**
   * Em que sala o médico está nesta faixa (spec 018) — id de
   * `config/agenda.salas`. Ausente = faixa de antes das salas (a tela
   * estima pela sala preferida) ou clínica sem salas cadastradas. O
   * backend do portal ignora: a trava dele é pelo NÚMERO de consultórios.
   */
  salaId?: string;
}

/** Faixa liberada dentro de uma data. `end` é EXCLUSIVO. */
export interface ReleasedRange {
  start: string;
  end: string;
  full: boolean;
  returnsMax?: number;
  procedures?: boolean;
  /** Reserva da secretária — fora do portal. Ver `ReleaseEntry`. */
  reserved?: boolean;
  /** De quem é a faixa. Ausente = de todos. Ver `ReleaseEntry`. */
  professionalId?: string;
  /** Sala da faixa (spec 018). Ver `ReleaseEntry`. */
  salaId?: string;
}

/** `Map<'YYYY-MM-DD', ReleasedRange[]>` a partir do campo cru. Entradas
 *  inválidas são ignoradas (lado seguro: não libera o que não entendeu). */
export function parseReleases(
  raw: unknown,
  shiftBoundary: string = DEFAULT_SHIFT_BOUNDARY,
): Map<string, ReleasedRange[]> {
  const out = new Map<string, ReleasedRange[]>();
  if (!Array.isArray(raw)) return out;
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Partial<ReleaseEntry>;
    if (typeof e.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(e.date)) continue;
    const extra = {
      returnsMax: typeof e.returnsMax === 'number' && e.returnsMax >= 0 ? Math.floor(e.returnsMax) : undefined,
      procedures: e.procedures === true ? true : undefined,
      reserved: e.reserved === true ? true : undefined,
      professionalId:
        typeof e.professionalId === 'string' && e.professionalId.trim()
          ? e.professionalId.trim()
          : undefined,
      salaId: typeof e.salaId === 'string' && e.salaId.trim() ? e.salaId.trim() : undefined,
    };
    let range: ReleasedRange | null = null;
    switch (e.scope) {
      case 'MORNING': range = { start: '00:00', end: shiftBoundary, full: false, ...extra }; break;
      case 'AFTERNOON': range = { start: shiftBoundary, end: '23:59', full: false, ...extra }; break;
      case 'CUSTOM':
        if (e.start && e.end && /^\d{2}:\d{2}$/.test(e.start) && /^\d{2}:\d{2}$/.test(e.end)
          && timeToMinutes(e.end) > timeToMinutes(e.start)) {
          range = { start: e.start, end: e.end, full: false, ...extra };
        }
        break;
      case 'FULL': range = { start: '00:00', end: '23:59', full: true, ...extra }; break;
      default: break;
    }
    if (!range) continue;
    const arr = out.get(e.date) ?? [];
    arr.push(range);
    out.set(e.date, arr);
  }
  return out;
}

/**
 * Recorta o mapa de liberações pra UM médico (spec 010, fase 4).
 *
 * Passa quem é dele e quem é de todos (`professionalId` ausente). Com
 * `professionalId` nulo — a recepção olhando "todos os médicos" — devolve
 * o mapa inteiro: o dia está liberado se ALGUÉM liberou. É o que faz a
 * visão geral continuar mostrando o consultório aberto num dia em que só
 * um dos médicos atende.
 *
 * Datas que ficam sem nenhuma faixa saem do mapa — e mapa sem a data é
 * exatamente "dia não liberado" pro resto do módulo, sem caso especial.
 */
export function releasesForProfessional(
  releases: Map<string, ReleasedRange[]>,
  professionalId: string | null | undefined,
): Map<string, ReleasedRange[]> {
  if (!professionalId) return releases;
  const out = new Map<string, ReleasedRange[]>();
  for (const [date, ranges] of releases) {
    const mine = ranges.filter((r) => !r.professionalId || r.professionalId === professionalId);
    if (mine.length) out.set(date, mine);
  }
  return out;
}

/**
 * Desdobra a faixa ANTIGA "para todos os médicos" (sem dono) em uma cópia
 * POR médico de `ids` (spec 018, 17/10/2026). É o que deixa mexer na agenda
 * de UM médico sem a faixa de todos continuar valendo por cima da edição —
 * e sem tirar nada dos colegas: cada um fica com o mesmo horário que tinha.
 * Sem faixa sem dono, devolve o MESMO array (quem chama compara por
 * referência pra saber se desdobrou).
 */
export function splitAllDoctorsRanges(ranges: ReleasedRange[], ids: string[]): ReleasedRange[] {
  if (!ids.length || !ranges.some((r) => !r.professionalId)) return ranges;
  return [
    ...ranges.filter((r) => r.professionalId),
    ...ranges.filter((r) => !r.professionalId).flatMap((r) => ids.map((id) => ({ ...r, professionalId: id }))),
  ];
}

/**
 * Complemento das faixas liberadas de uma data → faixas BLOQUEADAS.
 * Sem liberação nenhuma = dia inteiro fechado. Marca `reason` com
 * `UNRELEASED_REASON` pra UI diferenciar de bloqueio manual.
 */
export function unreleasedRanges(released: ReleasedRange[] | undefined): BlockedRange[] {
  if (!released?.length) return [{ start: '00:00', end: '23:59', full: true, reason: UNRELEASED_REASON }];
  if (released.some((r) => r.full)) return [];
  const sorted = [...released]
    .map((r) => ({ s: timeToMinutes(r.start), e: timeToMinutes(r.end) }))
    .sort((a, b) => a.s - b.s);
  const out: BlockedRange[] = [];
  let cursor = 0;
  for (const r of sorted) {
    if (r.s > cursor) out.push({ start: minutesToHHMM(cursor), end: minutesToHHMM(r.s), full: false, reason: UNRELEASED_REASON });
    cursor = Math.max(cursor, r.e);
  }
  const dayEnd = 23 * 60 + 59;
  if (cursor < dayEnd) out.push({ start: minutesToHHMM(cursor), end: '23:59', full: false, reason: UNRELEASED_REASON });
  return out;
}

function minutesToHHMM(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Faixas bloqueadas efetivas de UMA data: bloqueio manual ∪ não liberado. */
export function effectiveBlockedRanges(
  dateStr: string,
  blocked: Map<string, BlockedRange[]>,
  releases: Map<string, ReleasedRange[]>,
  releaseMode: boolean,
): BlockedRange[] | undefined {
  const manual = blocked.get(dateStr);
  if (!releaseMode) return manual;
  const unreleased = unreleasedRanges(releases.get(dateStr));
  if (!manual?.length) return unreleased.length ? unreleased : undefined;
  return [...manual, ...unreleased];
}

/**
 * Materializa o mapa efetivo pra uma janela de datas (hoje → +N dias).
 * Datas passadas ficam como estão: pintar o histórico de "não liberado"
 * só atrapalha quem consulta a agenda de meses atrás.
 */
export function buildEffectiveBlockedMap(
  blocked: Map<string, BlockedRange[]>,
  releases: Map<string, ReleasedRange[]>,
  releaseMode: boolean,
  daysAhead = 400,
  today: Date = new Date(),
): Map<string, BlockedRange[]> {
  if (!releaseMode) return blocked;
  const out = new Map<string, BlockedRange[]>(blocked);
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  for (let i = 0; i <= daysAhead; i++) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const eff = effectiveBlockedRanges(key, blocked, releases, true);
    if (eff?.length) out.set(key, eff); else out.delete(key);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** True quando a data está fechada só por falta de liberação (não por
 *  bloqueio manual) — banner e cor diferentes na agenda. */
export function isUnreleasedOnly(ranges: BlockedRange[] | undefined): boolean {
  return !!ranges?.length && ranges.every((r) => r.reason === UNRELEASED_REASON);
}

/**
 * Normaliza as faixas liberadas de UMA data: descarta inválidas, ordena e
 * funde o que se sobrepõe ou encosta (08:00–12:00 + 12:00–14:00 = uma faixa).
 *
 * Existe porque a tela de liberação passou a gravar VÁRIAS faixas por dia
 * (ex.: 08:00–12:00 e 14:00–17:00). Sem fundir, reaplicar a mesma faixa ia
 * empilhando entradas equivalentes em `releases[]` a cada clique — a agenda
 * daria no mesmo, mas o documento cresceria à toa e a célula do calendário
 * listaria a mesma coisa duas vezes.
 *
 * Faixas de DONOS diferentes nunca se fundem (spec 010): cada médico tem a
 * sua, e a faixa "de todos" (sem `professionalId`) é uma terceira coisa —
 * fundir apagaria de quem é o quê.
 *
 * Na fusão, `returnsMax` fica o MENOR (lado seguro: vale o teto mais
 * apertado que o médico pediu) e `procedures` é OU lógico.
 *
 * Faixa RESERVADA (só secretária) também não se funde com faixa comum:
 * fundir apagaria o "fora do portal" de um pedaço da faixa. Nem faixas em
 * SALAS diferentes (spec 018): manhã no C1 + tarde no C2 são duas coisas.
 */
export function mergeReleasedRanges(ranges: ReleasedRange[]): ReleasedRange[] {
  const byOwner = new Map<string, ReleasedRange[]>();
  for (const r of ranges) {
    if (!r.full && timeToMinutes(r.end) <= timeToMinutes(r.start)) continue;
    const owner = `${r.professionalId ?? ''}|${r.reserved ? 'R' : ''}|${r.salaId ?? ''}`;
    const arr = byOwner.get(owner) ?? [];
    arr.push(r);
    byOwner.set(owner, arr);
  }
  const out: ReleasedRange[] = [];
  for (const group of byOwner.values()) out.push(...mergeSameOwner(group));
  return out;
}

/** Fusão dentro de um único dono — o miolo de `mergeReleasedRanges`. */
function mergeSameOwner(valid: ReleasedRange[]): ReleasedRange[] {
  if (!valid.length) return [];
  const full = valid.find((r) => r.full);
  // Dia inteiro absorve o resto; só os metadados de todas sobrevivem.
  if (full) return [{ ...full, ...foldMeta(valid) }];
  const sorted = [...valid].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
  const out: ReleasedRange[] = [];
  let group: ReleasedRange[] = [sorted[0]];
  let cursorEnd = timeToMinutes(sorted[0].end);
  const flush = () => {
    out.push({
      start: group[0].start,
      end: minutesToHHMM(cursorEnd),
      full: false,
      professionalId: group[0].professionalId,
      reserved: group[0].reserved,
      salaId: group[0].salaId,
      ...foldMeta(group),
    });
  };
  for (const r of sorted.slice(1)) {
    if (timeToMinutes(r.start) <= cursorEnd) {
      group.push(r);
      cursorEnd = Math.max(cursorEnd, timeToMinutes(r.end));
    } else {
      flush();
      group = [r];
      cursorEnd = timeToMinutes(r.end);
    }
  }
  flush();
  return out;
}

/** Junta `returnsMax` (o menor) e `procedures` (OU) de um grupo de faixas. */
function foldMeta(group: ReleasedRange[]): Pick<ReleasedRange, 'returnsMax' | 'procedures'> {
  const caps = group.map((r) => r.returnsMax).filter((v): v is number => typeof v === 'number');
  return {
    returnsMax: caps.length ? Math.min(...caps) : undefined,
    procedures: group.some((r) => r.procedures) ? true : undefined,
  };
}

/** Faixas reservadas pra secretária (fora do portal) de uma data. */
export function reservedRanges(released: ReleasedRange[] | undefined): ReleasedRange[] {
  return (released ?? []).filter((r) => r.reserved);
}

/** A data tem alguma liberação com "dia de procedimento"? */
export function isProcedureDay(released: ReleasedRange[] | undefined): boolean {
  return !!released?.some((r) => r.procedures);
}

/** Teto de retornos do dia: o menor `returnsMax` informado, senão o default. */
export function returnsMaxFor(released: ReleasedRange[] | undefined, fallback: number): number {
  const vals = (released ?? []).map((r) => r.returnsMax).filter((v): v is number => typeof v === 'number');
  return vals.length ? Math.min(...vals) : fallback;
}
